window.APP = window.APP || {};
import './styles.css';
import { mount, unmount } from 'svelte';
import App from './App.svelte';
import { db } from './firebase-config.js';
import { doc, getDoc } from 'firebase/firestore';
import { isNativeApp } from './platform.js';
import { APP_VERSION, APP_VERSION_CODE, PLAY_STORE_URL } from './app-config.js';
import { toast, alertDialog, confirmDialog } from './ui/dialogs.js';
import { installGlobalErrorHandlers } from './ui/error-log.js';
import { initCrashReporter, setCrashReporterUser } from './services/crash-reporter.js';
import { effectivePlan } from './entitlements.js';

// Crash black box — capture every uncaught error before anything else runs.
installGlobalErrorHandlers();
initCrashReporter();

import { getAudioCtx } from './runtime-audio.js';

function unlockAudioOnFirstGesture() {
  const events = ['pointerdown', 'touchstart', 'click', 'keydown'];
  const unlock = () => {
    // Remove ALL of them together. With per-listener { once: true } the other
    // events stayed registered and fired again on the same interaction.
    events.forEach(evt => window.removeEventListener(evt, unlock));
    try {
      const ctx = getAudioCtx();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
    } catch (_) {}
  };
  events.forEach(evt => window.addEventListener(evt, unlock));
}
unlockAudioOnFirstGesture();

let _svelteApp = null;

function renderForceUpdateScreen() {
  if (_svelteApp) {
    try { unmount(_svelteApp); } catch (_) {}
    _svelteApp = null;
  }
  const actionAttr = isNativeApp
    ? `onclick="window.location.href='${PLAY_STORE_URL}'"`
    : `onclick="window.location.reload()"`;
  const actionLabel = isNativeApp ? 'Update on Play Store' : 'Refresh Now';
  document.body.innerHTML = `
    <div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100vh; background:#0d1b2a; color:white; font-family:sans-serif; text-align:center; padding:20px;">
      <div style="font-size:48px; margin-bottom:20px;">🔄</div>
      <h1 style="color:#fbbf24; margin:0 0 10px 0;">Update Required</h1>
      <p style="margin-bottom:20px; color:#cbd5e1; max-width:300px; line-height:1.5;">You are using an outdated version of FFPL Cricket Scorer (v${APP_VERSION}). Please update to continue.</p>
      <button ${actionAttr} style="background:#10b981; color:white; border:none; padding:14px 26px; font-size:16px; border-radius:10px; font-weight:bold; cursor:pointer;">${actionLabel}</button>
    </div>
  `;
}

async function checkForceUpdate() {
  try {
    const configRef = doc(db, 'config', 'appSettings');
    const snap = await getDoc(configRef);
    if (snap.exists()) {
      const data = snap.data();
      const minVersion = data.minRequiredVersion || 1;
      if (APP_VERSION_CODE < minVersion) {
        return true; // blocked
      }
      // Soft nudge: a newer version exists but is not mandatory —
      // show a dismissible banner on Home instead of blocking.
      const latest = Number(data.latestVersionCode) || 0;
      if (APP_VERSION_CODE < latest) {
        import('./svelte/store.js').then(({ updateAvailable }) => updateAvailable.set(true)).catch(() => {});
      }
    }
  } catch (err) {
    console.warn("Force update check failed:", err);
  }
  return false;
}

// Gate the app on the force-update check, but never let a slow network hold
// the launch hostage: after 3s we mount anyway and let the check finish in
// the background (a late "blocked" result still replaces the UI).
const _forceUpdatePromise = checkForceUpdate();
const _blockedNow = await Promise.race([
  _forceUpdatePromise,
  new Promise(resolve => setTimeout(() => resolve(false), 3000))
]);

if (_blockedNow) {
  renderForceUpdateScreen();
} else {
  _forceUpdatePromise.then(blocked => { if (blocked) renderForceUpdateScreen(); });
  _svelteApp = mount(App, {
    target: document.getElementById('app')
  });
}

import { initRouter } from './router.js';
import { teams } from './teams-data.js';
import { availableTeams, currentScreen, peerRole, peerRoomCode } from './svelte/store.js';
import { cloudResumeMatch, getMeta, loadFromStorage, persist } from './store.js';
import { get } from 'svelte/store';
import { activeMatch, settings } from './svelte/store.js';
import { PeerSync } from './peer-sync.js';
import { loadDeviceSession } from './session-storage.js';
import { setupRecorderPeerHandlers } from './sync/peer-controller.js';
import { resumeScreenForMatch } from './device-session.js';
import { isInningsOver } from './scoring-logic.js';
import { loadRecoverableRecordings } from './media/recording-safety-service.js';
import { listPendingGallerySessions } from './media/direct-gallery-recovery.js';
import { inspectRenderProcessRecovery } from './media/render-process-recovery.js';
import { auth } from './firebase-config.js';
import { onAuthStateChanged, getRedirectResult } from 'firebase/auth';
import { currentUser, savedMatches, authInitialized } from './svelte/store.js';
import { fetchMatchesFromCloud, fetchUserProfile, initSyncQueue, processSyncQueue } from './services/cloud-sync.js';
import { initFixtureResultRetry, retryPendingFixtureResults } from './services/tournament-cloud.js';
import { persistSaved } from './store.js';

// ─── Android hardware back button ───────────────────────────────────────
// Without this listener the system back button instantly kills the app —
// even mid-match. Map "back" to the logical parent screen instead.
if (isNativeApp) {
  import('@capacitor/app').then(({ App: CapApp }) => {
    CapApp.addListener('backButton', async () => {
      // Close transient UI first. Without this, pressing Back with a forced
      // modal open (Select Bowler after an over, the Wicket Wizard mid-entry)
      // navigated away and abandoned the over transition.
      const { modalState, fullScreenBoard, showWagonWheel, showPremiumModal } = await import('./svelte/store.js');
      if (get(modalState)) { modalState.set(null); return; }
      if (get(showPremiumModal)) { showPremiumModal.set(false); return; }
      if (get(showWagonWheel)) { showWagonWheel.set(false); return; }
      if (get(fullScreenBoard)) { fullScreenBoard.set(null); return; }

      const screen = get(currentScreen);
      if (screen === 'home' || !screen) {
        CapApp.minimizeApp();
        return;
      }
      if (screen === 'scoring') {
        const exit = await confirmDialog('Exit to home? Match progress will be saved.', { title: 'Leave scoring' });
        if (!exit) return;
        persist({ immediate: true });
        currentScreen.set('home');
        return;
      }
      if (screen === 'recording') {
        toast('Use the on-screen Exit button to leave recording safely.');
        return;
      }
      currentScreen.set('home');
    });
  }).catch(err => console.warn('[BackButton] Capacitor App plugin unavailable:', err));
}

// Complete any pending signInWithRedirect (mobile-PWA / popup-blocked fallback
// in LoginModal). Without this the redirect credential is never consumed and
// any error is swallowed silently — surface it in the console instead.
const redirectPromise = getRedirectResult(auth).catch(err => {
  console.error('[Auth] redirect result failed:', err);
  return null;
});

// Setup global auth listener
onAuthStateChanged(auth, async (user) => {
  if (user) {
    setCrashReporterUser(user.uid);
    // Initially set without plan
    currentUser.set({
      uid: user.uid,
      phoneNumber: user.phoneNumber,
      displayName: user.displayName,
      email: user.email,
      plan: effectivePlan(null)
    });
    authInitialized.set(true);

    // Fetch profile (which contains plan)
    const profile = await fetchUserProfile();
    if (profile) {
      currentUser.update(u => ({ ...u, plan: effectivePlan(profile) }));
    }

    // Fetch their cloud backup
    const cloudMatches = await fetchMatchesFromCloud();
    if (cloudMatches.length > 0) {
       const currentSaved = get(savedMatches);
       const merged = [...currentSaved];
       cloudMatches.forEach(cm => {
         const existingIndex = merged.findIndex(m => m.id === cm.id);
         if (existingIndex === -1) {
           merged.push(cm);
         } else {
           const existingMatch = merged[existingIndex];
           if ((Number(cm._stateRevision) || 0) > (Number(existingMatch._stateRevision) || 0)) {
             merged[existingIndex] = cm;
           }
         }
       });

       savedMatches.set(merged);
       persistSaved();
    }
  } else {
    // If they appear logged out, wait to see if a redirect login is just finishing
    await redirectPromise;
    if (auth.currentUser) return; // The redirect actually worked, onAuthStateChanged will fire again

    setCrashReporterUser(null);
    currentUser.set(null);
    authInitialized.set(true);
  }
});

// Ask the browser to protect our storage from eviction — an active match and
// its recovery checkpoints must survive storage pressure (videos, other apps).
// iOS Safari and Chrome REQUIRE a user gesture: calling this at load silently
// fails, and the OS later wipes IndexedDB when space runs low. Defer to the
// first real interaction instead.
function _requestPersistentStorage() {
  try {
    navigator.storage?.persist?.()
      .then(granted => console.log('[Storage] persistent storage granted:', granted))
      .catch(() => {});
  } catch (_) {}
}
if (navigator.storage?.persisted) {
  navigator.storage.persisted().then(already => {
    if (already) return;
    const once = () => {
      _requestPersistentStorage();
      window.removeEventListener('pointerdown', once);
      window.removeEventListener('keydown', once);
    };
    window.addEventListener('pointerdown', once, { once: true });
    window.addEventListener('keydown', once, { once: true });
  }).catch(() => {});
}

// OFFLINE SMART SYNC: Firestore's persistent cache (firebase-config.js) queues
// offline writes automatically; these extra queues retry the transaction-based
// writes (which the cache does NOT queue) when the network returns.
initSyncQueue();
initFixtureResultRetry();
setTimeout(() => processSyncQueue().catch(() => {}), 5000);
setTimeout(() => retryPendingFixtureResults().catch(() => {}), 6000);

// Fetch logos lazily to reduce initial bundle size
fetch("/teams-logos.json").then(r => r.json()).then(logos => {
  teams.forEach(t => {
    if (t.logoKey && logos[t.logoKey]) {
      t.logo = logos[t.logoKey];
    }
  });
  availableTeams.set(teams.map(t => {
    const override = getMeta(t.name);
    return { ...t, ...override };
  }));
}).catch(e => console.error("Logo fetch error", e));

await loadFromStorage();
if (isNativeApp) {
  const marker = await inspectRenderProcessRecovery();
  if (marker.pending) {
    const { renderProcessRecovery } = await import('./svelte/store.js');
    renderProcessRecovery.set(marker);
    console.warn('[RecordingRecovery] WebView renderer interruption detected:', marker.reason);
  }
  const sessions = await listPendingGallerySessions().catch(error => {
    console.warn('Pending Gallery recording scan failed:', error);
    return [];
  });
  const { pendingGallerySessions, directGalleryStatus } = await import('./svelte/store.js');
  pendingGallerySessions.set(sessions);
  directGalleryStatus.update(status => ({
    ...status,
    capability: 'available',
    state: sessions.length ? 'recovery-pending' : status.state
  }));
}
await loadRecoverableRecordings().catch(error => console.warn('Interrupted recording scan failed:', error));
initRouter();
window.APP.ready = true;

// Handle the QR-code / shared "resume" link: <app-url>?resume=<code>.
// (The Home screen has been generating these links; consume them here.)
const _resumeCode = new URLSearchParams(window.location.search).get('resume');
if (_resumeCode) {
  // Strip the param so a reload doesn't re-trigger the import.
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);
  } catch (_) {}
  cloudResumeMatch(_resumeCode.trim()).then(async remote => {
    if (!remote) {
      await alertDialog('Could not load the match for this resume link (invalid code or expired sync).', { title: 'Resume link' });
      return;
    }
    const current = get(activeMatch);
    if (current && current.id === remote.id &&
        (Number(current._stateRevision) || 0) >= (Number(remote._stateRevision) || 0)) {
      // Local copy is the same match and at least as new — keep it.
    } else {
      if (current && current.id !== remote.id &&
          !(await confirmDialog('Resume the match from this link? Your current active match will be replaced.', { title: 'Resume match' }))) {
        return;
      }
      activeMatch.set(remote);
      persist();
    }
    const m = get(activeMatch);
    currentScreen.set(resumeScreenForMatch(m, get(peerRole), isInningsOver(m)));
  }).catch(err => {
    console.warn('[CricketScorer] Resume link failed:', err);
    alertDialog('Network error while loading the resume link. Use "Cloud Restore" on the Home screen.', { title: 'Resume link' });
  });
}

const active = get(activeMatch);
const deviceSession = loadDeviceSession();
if (active?._deviceRole === 'scorer' && active?._peerRoomCode) {
  PeerSync.restoreScorerSession(active).catch(err => {
    console.warn('[PeerSync] Saved scorer session could not reconnect yet:', err?.message || err);
  });
} else if (deviceSession?.role === 'recorder' && deviceSession.roomCode) {
  setupRecorderPeerHandlers();
  peerRole.set('recorder');
  peerRoomCode.set(deviceSession.roomCode);
  currentScreen.set('recording');
  PeerSync.joinRoom(deviceSession.roomCode).catch(err => {
    console.warn('[PeerSync] Saved recorder session could not reconnect yet:', err?.message || err);
  });
}

// Flush active match persistence queue immediately on unload or page hide
const flushPersist = () => {
  try {
    persist({ immediate: true, syncPeer: false });
  } catch (err) {
    console.warn('[CricketScorer] Failed to commit state on page unload:', err);
  }
};
window.addEventListener('beforeunload', flushPersist);
window.addEventListener('pagehide', flushPersist);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    flushPersist();
  }
});

// For E2E testing
import { createMatch, setOpeners, recordRuns, recordWicket, setNewBatsman, computeResult, startSecondInnings } from './scoring-logic.js';
import { generateBallCommentary } from './commentary-engine.js';
import { checkLowerThirdTrigger } from './lower-third-engine.js';

window.__E2E__ = {
  activeMatch,
  settings,
  createMatch,
  setOpeners,
  recordRuns,
  recordWicket,
  setNewBatsman,
  computeResult,
  startSecondInnings,
  generateBallCommentary,
  checkLowerThirdTrigger
};
